import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { Search, Send } from 'react-feather';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { CheckCircle, CircleDashed, Plus } from 'phosphor-react';
import { AddPaymentRequestModal } from './AddPaymentRequestModal/AddPaymentRequestModal';
import { getPaymentRequests } from '../../../../redux/services/paymentRequestsService';
import PaymentRequestsTable from './PaymentRequestsTable/PaymentRequestsTable';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../redux/services/suppliersService';
import {
  Buyer,
  canUserAccessBuyerBranch,
  collectBranchesFromBuyers,
  DillPaymentRequestResponse,
  DocumentsSummary,
} from '@dill/dill-shared';
import moment from 'moment';
import { COLORS } from '../../../../utils/colors';
import AppDatePicker from '../../creditApplication/CreditApplicationComponents/AppDatePicker';
import { DillButton, DillCheckbox, DillDropDownFilter } from '../../../general/Dill';

function PaymentRequests() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchText, setSearchText] = useState('');
  const [isAddPaymentRequestModalOpen, setIsAddPaymentRequestModalOpen] = useState(false);
  const { paymentRequests } = useAppSelector((state) => state.paymentRequests);
  const { user, firebaseUserId } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  // Tracks the active supplier id for in-flight customer fetches, so a chain started for a
  // previous supplier stops once the supplier is switched (otherwise its results would be
  // written into the now differently-scoped store). The request ref lets us abort in-flight.
  const activeFetchSupplierIdRef = useRef<string | null>(currentSupplier?.id ?? null);
  const inFlightCustomersRequestRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);
  useEffect(() => () => inFlightCustomersRequestRef.current?.abort(), [currentSupplier?.id]);
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [customerCount, setCustomerCount] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');
  const [searchedPaymentRequests, setSearchedPaymentRequests] = useState<
    DillPaymentRequestResponse[]
  >([]);
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });

  useEffect(() => {
    if (user && currentSupplier?.id) {
      dispatch(getPaymentRequests({ supplierId: currentSupplier?.id || '' }));
    }
    return () => {};
  }, [user?.id, currentSupplier?.id]);

  useEffect(() => {
    if (paymentRequests || searchText) {
      const searched = [...paymentRequests]
        .filter((paymentRequestDetails) => {
          if (
            (paymentRequestDetails?.buyer?.name &&
              paymentRequestDetails?.buyer?.name
                .toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (currentSupplier?.integrationType !== 'RUTTER_QBD' &&
              paymentRequestDetails?.buyer?.quickbooksCustomerId &&
              paymentRequestDetails.buyer.name &&
              `${paymentRequestDetails.buyer?.name} - ${paymentRequestDetails.buyer?.quickbooksCustomerId}`
                .toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails.emails &&
              paymentRequestDetails?.emails.some((emailItem) =>
                emailItem.toString().toLowerCase().includes(searchText.toLowerCase())
              )) ||
            (paymentRequestDetails?.notes &&
              paymentRequestDetails?.notes
                .toString()
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails?.status &&
              paymentRequestDetails?.status
                .toString()
                .replaceAll('_', ' ')
                .replaceAll('UNPAID', 'SENT')
                .toLowerCase()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails?.balance &&
              Number(paymentRequestDetails?.balance)
                .toFixed(2)
                .toString()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails?.totalAmount &&
              Number(paymentRequestDetails?.totalAmount)
                .toFixed(2)
                .toString()
                .includes(searchText.toLowerCase())) ||
            (paymentRequestDetails.createdAt &&
              moment(paymentRequestDetails.createdAt)
                .format('MM/DD/YYYY')
                .toLowerCase()
                .includes(searchText.toLowerCase()))
          ) {
            return true;
          }
          return false;
        })
        .sort((A, B) => {
          if (!A.createdAt || !B.createdAt) {
            return -1;
          }
          return new Date(B.createdAt).getTime() - new Date(A.createdAt).getTime();
        });
      setSearchedPaymentRequests(searched);
    }
  }, [paymentRequests, searchText]);

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        userId: string;
        isInvitedUser: boolean;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[],
    supplierId: string
  ) => {
    if (firstCustomers.length > count) {
      return;
    }
    setCustomerCount(count);
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      // A newer fetch (e.g. after switching suppliers) supersedes this loop — stop so we
      // don't write the previous supplier's customers into the store.
      if (!supplierId || activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      const request = dispatch(
        recursiveGetUserCustomers({
          supplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled') {
        if (results.payload) {
          const payload = results.payload as any;
          const fetchedCustomers: {
            buyer: Buyer;
            usersEmailsDetails: {
              email: string;
              isInvitedUser: boolean;
              isStatementsEmailOptOut: boolean;
              isPaymentRemindersEmailOptOut: boolean;
            }[];
            documentsSummary: DocumentsSummary;
          }[] = payload.data as any;
          const lastDocumentId = payload.lastDocumentId;
          if (lastDocumentId !== currentPreviousLastDocumentId) {
            setPreviousLastDocumentId(lastDocumentId);
            currentPreviousLastDocumentId = lastDocumentId;
          }
          if (!lastDockId || fetchedCustomers.length < pageSize) {
            console.log('No more pages to fetch');
            break;
          }
        }
      } else {
        console.log('Fetch failed or interrupted');
        break;
      }
    }
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoading(true);
    const pageSize = 100;
    let currentPreviousLastDocumentId = previousLastDocumentId;
    if (loadOptions && loadOptions?.isFreshFetch) {
      currentPreviousLastDocumentId = '';
    }
    const request = dispatch(
      getUserCustomers({
        supplierId: fetchSupplierId,
        previousLastDocumentId: currentPreviousLastDocumentId,
        pageSize,
        sortBy: 'name',
        sortOrder: 'asc',
      })
    );
    inFlightCustomersRequestRef.current = request;
    const results = await request;
    setLoading(false);
    if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
      return;
    }
    if (results.type === 'supplier/getUserCustomers/fulfilled') {
      if (results.payload) {
        const payload = results.payload as any;
        const cust: {
          buyer: Buyer;
          usersEmailsDetails: {
            email: string;
            userId: string;
            isInvitedUser: boolean;
            isStatementsEmailOptOut: boolean;
            isPaymentRemindersEmailOptOut: boolean;
          }[];
          documentsSummary: DocumentsSummary;
        }[] = payload.data as any;
        const lastDockId = payload.lastDocumentId;
        if (!customerCount && payload.customerCount) {
          setCustomerCount(payload.customerCount || 0);
        }
        if (lastDockId && lastDockId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDockId);

          const count = Number(payload.customerCount);
          recursiveFetchCustomers(count, lastDockId, [...cust], fetchSupplierId);
        }
      }
    }
  };

  useEffect(() => {
    if (mainRoles?.includes('SUPPLIER') && firebaseUserId) {
      setPreviousLastDocumentId('');
      handleLoadMore({ isFreshFetch: true });
    }

    return () => {};
  }, [user?.id, firebaseUserId, location.pathname]);

  const customers = useMemo(() => {
    const buyers = paymentRequests
      .map((paymentRequest) => paymentRequest?.buyer?.name ?? '')
      .filter((buyer) => buyer !== '');

    return Array.from(new Set(buyers));
  }, [paymentRequests]);

  const paymentStatus = useMemo(() => {
    const status = paymentRequests.map((paymentRequest) => paymentRequest.status);

    return Array.from(new Set(status));
  }, [paymentRequests]);

  // Branches come from the customers on the payment requests themselves — SupplierAdditionalDetails'
  // branches is hand-entered and incomplete. Anything the user isn't allowed to see is already absent from
  // `paymentRequests` (the backend scopes it), so this list is inherently limited to their branches.
  const filterableBranches = useMemo(
    () =>
      collectBranchesFromBuyers(
        paymentRequests
          .map((paymentRequest) => paymentRequest.buyer)
          .filter((buyer) =>
            canUserAccessBuyerBranch({
              user,
              supplierId: currentSupplier?.id,
              buyerBranch: buyer?.branch,
            })
          )
      ),
    [paymentRequests, user, currentSupplier?.id]
  );

  const handleSelectFilter = (selected: { [key: string]: string[] }) => {
    setSelectedFilters(selected);
    if (dateRange?.from && dateRange?.to && selected?.dateRequested?.length === 0) {
      setDateRange({ from: null, to: null });
    }

    const hasSelections = Object.values(selected).some((arr) => (arr?.length ?? 0) > 0);
    if (!hasSelections && !dateRange.from && !dateRange.to) {
      setSearchedPaymentRequests(paymentRequests);
      return;
    }

    const filtered = paymentRequests.filter((application) => {
      const matchesFilters = Object.entries(selected).every(([key, values]) => {
        if (!values || values.length === 0) return true;

        if (key === 'dateRequested' && values.length === 0) {
          return values;
        } else if (key === 'dateRequested') {
          const matchesDate =
            !dateRange.from ||
            !dateRange.to ||
            (moment(application.createdAt).isSameOrAfter(dateRange.from, 'day') &&
              moment(application.createdAt).isSameOrBefore(dateRange.to, 'day'));
          return matchesDate;
        }

        if (key === 'customer') {
          const customerName = application?.buyer?.name ?? '';
          return values.includes(customerName);
        }

        if (key === 'branch') {
          // The branch lives on the customer, not the payment request.
          return values.includes(application?.buyer?.branch ?? '');
        }

        const appValueRaw = (application as Record<string, unknown>)[key];
        if (appValueRaw == null) return false;

        const appValue = String(appValueRaw).toLowerCase();
        return values.some((v) => v.toLowerCase() === appValue);
      });

      return matchesFilters;
    });

    setSearchedPaymentRequests(filtered);
  };

  const toolbarLeft = (
    <div className="w-[280px]">
      <DillDropDownFilter
        placeholder="Filters"
        closeMenu={false}
        isMulti
        showSelectedItems
        multiValue={selectedFilters}
        inputHeight={34}
        items={[
          // The menu is only `max-h-60` tall and the date-range block fills most of it, so a group
          // placed after it falls below the scroll fold and looks empty. Keep branch first.
          ...(filterableBranches.length > 1
            ? [
                {
                  id: 'branch',
                  label: 'Filter by branch',
                  value: filterableBranches.map((branch) => ({
                    label: branch,
                    value: branch,
                    labelInElement: (
                      <div className="w-fit flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={selectedFilters?.branch?.includes(branch) ?? false}
                        />
                        <div className="text-xs">{branch}</div>
                      </div>
                    ),
                  })),
                },
              ]
            : []),
          {
            id: 'dateRequested',
            label: 'Filter by date requested',
            value: [
              {
                label: `${dateRange?.from ? moment(dateRange?.from).format('MM/DD/YY') : ''} - ${
                  dateRange?.to ? moment(dateRange?.to).format('MM/DD/YY') : ''
                }`,
                value: `${dateRange?.from ? moment(dateRange?.from).format('MM/DD/YY') : ''} - ${
                  dateRange?.to ? moment(dateRange?.to).format('MM/DD/YY') : ''
                }`,
                isDisabled: true,
                labelInElement: (
                  <div>
                    <div className="w-fit flex flex-row gap-2 text-black">
                      <AppDatePicker
                        selectedDate={dateRange?.from}
                        maximumDate={dateRange?.to || new Date()}
                        label={'From'}
                        onDateSelected={(date) => {
                          if (date) {
                            setDateRange((prev) => ({
                              ...prev,
                              from: date,
                            }));
                          }
                        }}
                      />
                      <AppDatePicker
                        selectedDate={dateRange?.to}
                        maximumDate={new Date()}
                        minumumDate={dateRange?.from}
                        label={'To'}
                        onDateSelected={(date) => {
                          if (date) {
                            setDateRange((prev) => ({
                              ...prev,
                              to: date,
                            }));
                          }
                        }}
                      />
                    </div>
                    <div>
                      <button
                        className="PRIMARY_500-CLR text-xs mt-1"
                        onClick={() => {
                          setDateRange({ to: null, from: null });
                          handleSelectFilter({
                            ...selectedFilters,
                            dateRequested: [],
                          });
                        }}>
                        Clear
                      </button>
                    </div>
                  </div>
                ),
              },
            ],
          },
          {
            id: 'status',
            label: 'Filter by status',
            value: [
              ...paymentStatus.map((item) => ({
                label:
                  item === 'PAID'
                    ? 'Paid'
                    : item === 'PAYMENT_INITIATED'
                    ? 'Payment Initiated'
                    : item === 'PATIALLY_PAID'
                    ? 'Partially Paid'
                    : 'Sent',
                value: item,
                labelInElement: (
                  <div className="w-fit flex flex-col">
                    {item === 'PAID' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={selectedFilters?.status?.includes('PAID') ?? false}
                        />
                        <div className="flex SUCCESS_100-BG text-xs SUCCESS_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                          <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                          <div className="ml-2 text-xs">Paid</div>
                        </div>
                      </div>
                    )}
                    {item === 'UNPAID' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={selectedFilters?.status?.includes('UNPAID') ?? false}
                        />
                        <div className="flex PRIMARY_100-BG text-xs PRIMARY_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                          <Send size={14} color={COLORS.PRIMARY_500} />
                          <div className="ml-2 text-xs">Sent</div>
                        </div>
                      </div>
                    )}
                    {item === 'PAYMENT_INITIATED' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={
                            selectedFilters?.status?.includes('PAYMENT_INITIATED') ?? false
                          }
                        />
                        <div className="flex INFO_100-BG text-xs INFO_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center text-nowrap">
                          <CircleDashed size={20} color={COLORS.INFO_500} />
                          <div className="ml-2 text-xs">Payment Initiated</div>
                        </div>
                      </div>
                    )}
                    {item === 'PATIALLY_PAID' && (
                      <div className="flex flex-row gap-1 items-center">
                        <DillCheckbox
                          isChecked={selectedFilters?.status?.includes('PATIALLY_PAID') ?? false}
                        />
                        <div className="flex SUCCESS_100-BG text-xs SUCCESS_500-CLR font-semibold px-2 py-1 rounded-full items-center justify-center">
                          <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                          <div className="ml-2 text-xs">Partially Paid</div>
                        </div>
                      </div>
                    )}
                  </div>
                ),
              })),
            ],
          },
        ]}
        itemWidth={180}
        showMinimalDesign
        onMultiSelectChange={(data) => {
          if (data) {
            const transformed = Object.fromEntries(
              Object.entries(data).map(([key, arr]) => [
                key,
                (arr as { value: string }[]).map((item) => item.value),
              ])
            );
            handleSelectFilter(transformed);
          }
        }}
      />
    </div>
  );

  const toolbarRight = (
    <div className="flex flex-row items-center gap-2">
      <DillButton
        label="Add Payment Request"
        onClick={() => {
          setIsAddPaymentRequestModalOpen(true);
        }}
        icon={<Plus size={16} />}
      />
    </div>
  );

  useEffect(() => {
    if (!(dateRange.from && dateRange.to)) return;
    handleSelectFilter({
      ...selectedFilters,
      dateRequested: [
        `${moment(dateRange?.from).format('MM/DD/YY')} - ${moment(dateRange?.to).format(
          'MM/DD/YY'
        )}`,
      ],
    });
    return () => {};
  }, [dateRange]);

  return (
    <div className="w-full h-full px-4 pb-4">
      <AddPaymentRequestModal
        open={isAddPaymentRequestModalOpen}
        handleClose={() => {
          setIsAddPaymentRequestModalOpen(false);
        }}
        onPaymentRequestAdded={() => {
          setIsAddPaymentRequestModalOpen(false);
          dispatch(getPaymentRequests({ supplierId: currentSupplier?.id || '' }));
        }}
      />
      <div style={{ height: '100%' }}>
        <PaymentRequestsTable
          paymentRequests={searchedPaymentRequests}
          toolbarLeft={toolbarLeft}
          toolbarRight={toolbarRight}
          height="100vh-280px"
        />
      </div>
    </div>
  );
}

export default PaymentRequests;
